import type { PartialPropsOf, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentSummary } from "shared/components/content/content-summary";

import { ContentActionBar } from "scmp-app/components/content/content-action-bar";
import { ContentAuthor } from "scmp-app/components/content/content-author";
import { ContentAuthorImage } from "scmp-app/components/content/content-author-image";
import { ContentCoverImage } from "scmp-app/components/content/content-cover-image";
import { ContentCoverVideo } from "scmp-app/components/content/content-cover-video";
import { ContentHeadline } from "scmp-app/components/content/content-headline";
import { ContentLiveTag } from "scmp-app/components/content/content-live-tag";
import { ContentPrintHeadline } from "scmp-app/components/content/content-print-headline";
import { ContentPublishedDate } from "scmp-app/components/content/content-published-date";
import { ContentReadingTime } from "scmp-app/components/content/content-reading-time";
import { ContentSections } from "scmp-app/components/content/content-sections";
import { ContentSeriesShortLabel } from "scmp-app/components/content/content-series-short-label";
import { ContentSponsorTag } from "scmp-app/components/content/content-sponsor-tag";
import { ContentSubHeadline } from "scmp-app/components/content/content-sub-headline";
import type { Variant as TopicLinkVariant } from "scmp-app/components/content/content-topic-link";
import { ContentTopicLink } from "scmp-app/components/content/content-topic-link";
import { ContentUpdatedDate } from "scmp-app/components/content/content-updated-date";
import type { SponsorTag } from "scmp-app/components/sponsor-tag";
import { VideoDurationOverlay } from "scmp-app/components/video/video-duration-overlay";
import { VideoPlayIcon } from "scmp-app/components/video/video-play-icon";
import type { hooksContentItemProvidersContent$key } from "scmp-app/queries/__generated__/hooksContentItemProvidersContent.graphql";

import { StyledGalleryIndicator } from "./styles";

export type DefaultComponentsProviders = {
  actionBar: (props?: PartialPropsOf<typeof ContentActionBar>) => ReactNode;
  author: (props?: PartialPropsOf<typeof ContentAuthor>) => ReactNode;
  authorImage: (props?: PartialPropsOf<typeof ContentAuthorImage>) => ReactNode;
  coverImage: (props?: PartialPropsOf<typeof ContentCoverImage>) => ReactNode;
  coverVideo: (props?: PartialPropsOf<typeof ContentCoverVideo>) => ReactNode;
  gallery?: {
    indicator: (props?: PartialPropsOf<typeof StyledGalleryIndicator>) => ReactNode;
  };
  headline: (props?: PartialPropsOf<typeof ContentHeadline>) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof ContentLiveTag>) => ReactNode;
  printHeadline: (props?: PartialPropsOf<typeof ContentPrintHeadline>) => ReactNode;
  publishedDate: (props?: PartialPropsOf<typeof ContentPublishedDate>) => ReactNode;
  readingTime: (props?: PartialPropsOf<typeof ContentReadingTime>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof ContentSections>) => ReactNode;
  seriesShortLabel?: (props?: PartialPropsOf<typeof ContentSeriesShortLabel>) => ReactNode;
  sponsorTag: (
    props?: PartialPropsOf<typeof ContentSponsorTag> & PartialPropsOf<typeof SponsorTag>,
  ) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof ContentSubHeadline>) => ReactNode;
  summary: (props?: PartialPropsOf<typeof ContentSummary>) => ReactNode;
  topicLink?: (props?: PartialPropsOf<typeof ContentTopicLink>) => ReactNode;
  updatedDate: (props?: PartialPropsOf<typeof ContentUpdatedDate>) => ReactNode;
  video?: {
    durationOverlay: (props?: PartialPropsOf<typeof VideoDurationOverlay>) => ReactNode;
    videoPlayIcon: (props?: PartialPropsOf<typeof VideoDurationOverlay>) => ReactNode;
  };
};

export const useContentItemProviders = (
  reference: hooksContentItemProvidersContent$key,
  topicLinkVariant?: TopicLinkVariant,
) => {
  const content = useFragment(
    graphql`
      fragment hooksContentItemProvidersContent on Content
      @argumentDefinitions(
        withActionBar: { type: "Boolean", defaultValue: false }
        withAuthorImage: { type: "Boolean", defaultValue: false }
        withAuthorLink: { type: "Boolean", defaultValue: false }
        withCoverImage: { type: "Boolean", defaultValue: false }
        withCoverImageLandscape250x99: { type: "Boolean", defaultValue: false }
        withCoverImageSize1200x800: { type: "Boolean", defaultValue: false }
        withCoverImageSize1200x1200: { type: "Boolean", defaultValue: false }
        withCoverImageSize640x360: { type: "Boolean", defaultValue: false }
        withCoverImageSize1280x720: { type: "Boolean", defaultValue: false }
        withCoverImageSize768x768: { type: "Boolean", defaultValue: false }
        withCoverImageSize540x360: { type: "Boolean", defaultValue: false }
        withCoverImageSize320x240: { type: "Boolean", defaultValue: false }
        withCoverImageSize660w: { type: "Boolean", defaultValue: false }
        withCoverImageSize320x180: { type: "Boolean", defaultValue: false }
        withCoverImageSize80x80: { type: "Boolean", defaultValue: false }
        withCoverImageSize500x500: { type: "Boolean", defaultValue: false }
        withCoverImageSize800x1200: { type: "Boolean", defaultValue: false }
        withCoverImageSize118x118: { type: "Boolean", defaultValue: false }
        withCoverVideo: { type: "Boolean", defaultValue: false }
        withHeadline: { type: "Boolean", defaultValue: false }
        withLiveTag: { type: "Boolean", defaultValue: false }
        withPrintHeadline: { type: "Boolean", defaultValue: false }
        withPublishedDate: { type: "Boolean", defaultValue: false }
        withSections: { type: "Boolean", defaultValue: false }
        withSponsorTag: { type: "Boolean", defaultValue: true }
        withSubHeadline: { type: "Boolean", defaultValue: false }
        withSummary: { type: "Boolean", defaultValue: false }
        withTopicLink: { type: "Boolean", defaultValue: false }
        withReadingTime: { type: "Boolean", defaultValue: false }
        withUpdatedDate: { type: "Boolean", defaultValue: false }
        withVideoDurationOverlay: { type: "Boolean", defaultValue: false }
        withGalleryOverlay: { type: "Boolean", defaultValue: false }
        withSeriesShortLabel: { type: "Boolean", defaultValue: false }
      ) {
        ...contentActionBarContent @include(if: $withActionBar)
        ...contentAuthorImageContent @include(if: $withAuthorImage)
        ...contentAuthorLinkContent @include(if: $withAuthorLink)
        ...contentCoverImageContent
          @arguments(
            withLandscape250x99: $withCoverImageLandscape250x99
            withSize1200x800: $withCoverImageSize1200x800
            withSize1200x1200: $withCoverImageSize1200x1200
            withSize640x360: $withCoverImageSize640x360
            withSize1280x720: $withCoverImageSize1280x720
            withSize768x768: $withCoverImageSize768x768
            withSize660w: $withCoverImageSize660w
            withSize540x360: $withCoverImageSize540x360
            withSize320x240: $withCoverImageSize320x240
            withSize320x180: $withCoverImageSize320x180
            withSize80x80: $withCoverImageSize80x80
            withSize500x500: $withCoverImageSize500x500
            withSize800x1200: $withCoverImageSize800x1200
            withSize118x118: $withCoverImageSize118x118
          )
          @include(if: $withCoverImage)
        ...contentCoverVideoContent @include(if: $withCoverVideo)
        ...contentHeadlineContent @include(if: $withHeadline)
        ...contentLiveTagContent @include(if: $withLiveTag)
        ...contentPrintHeadlineContent @include(if: $withPrintHeadline)
        ...contentPublishedDateContent @include(if: $withPublishedDate)
        ...contentSectionsContent @include(if: $withSections)
        ...contentSubHeadlineContent @include(if: $withSubHeadline)
        ...contentSummaryContent @include(if: $withSummary)
        ...contentTopicLinkContent @include(if: $withTopicLink)
        ...contentReadingTimeContent @include(if: $withReadingTime)
        ...contentSponsorTagContent @include(if: $withSponsorTag)
        ...contentUpdatedDateContent @include(if: $withUpdatedDate)
        ...contentSeriesShortLabelContent @include(if: $withSeriesShortLabel)
        ... on Video {
          __typename
          ...videoDurationOverlayVideo @include(if: $withVideoDurationOverlay)
        }
        ... on Gallery {
          __typename
          ...indicatorGalleryIndicatorGallery @include(if: $withGalleryOverlay)
        }
      }
    `,
    reference,
  );

  const providers: DefaultComponentsProviders = {
    actionBar: props => <ContentActionBar reference={content} {...props} />,
    author: props => <ContentAuthor reference={content} {...props} />,
    authorImage: props => <ContentAuthorImage reference={content} {...props} />,
    coverImage: props => (
      <ContentCoverImage
        reference={content}
        responsiveVariants={{
          desktopUp: "size80x80",
          mobileUp: "size80x80",
          tabletUp: "size80x80",
        }}
        {...props}
      />
    ),
    coverVideo: props => <ContentCoverVideo reference={content} {...props} />,
    headline: props => (
      <ContentHeadline reference={content} skipHeadlineSponsorStyle={false} {...props} />
    ),
    liveTag: props => <ContentLiveTag reference={content} {...props} />,
    printHeadline: props => <ContentPrintHeadline reference={content} {...props} />,
    publishedDate: props => <ContentPublishedDate reference={content} {...props} />,
    readingTime: props => <ContentReadingTime reference={content} {...props} />,
    sections: props => <ContentSections reference={content} {...props} />,
    seriesShortLabel: props => <ContentSeriesShortLabel reference={content} {...props} />,
    sponsorTag: props => <ContentSponsorTag reference={content} {...props} />,
    subHeadline: props => <ContentSubHeadline componentTag="h3" reference={content} {...props} />,
    summary: props => <ContentSummary componentTag="h3" reference={content} {...props} />,
    updatedDate: props => <ContentUpdatedDate reference={content} {...props} />,
  };

  if (topicLinkVariant) {
    providers.topicLink = props => (
      <ContentTopicLink reference={content} variant={topicLinkVariant} {...props} />
    );
  }

  if (content.__typename === "Video") {
    providers.video = {
      durationOverlay: props => (
        <VideoDurationOverlay
          reference={content}
          responsiveVariants={{ desktopUp: "medium", mobileUp: "small", tabletUp: "medium" }}
          variant="default"
          {...props}
        />
      ),
      videoPlayIcon: () => <VideoPlayIcon />,
    };
  } else if (content.__typename === "Gallery") {
    providers.gallery = {
      indicator: props => <StyledGalleryIndicator {...props} reference={content} />,
    };
  }

  return providers;
};
